import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/Modules/Core/Components/shadcn/popover";

type SplitButtonOption = {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
};

type SplitButtonProps = {
  primaryAction: React.ReactNode;
  menuOptions: SplitButtonOption[];
  onPrimaryClick: () => void;
  onSelect: (option: SplitButtonOption) => void;
  disabled?: boolean;
  className?: string;
  menuAlign?: "start" | "center" | "end";
  primaryClassName?: string;
  triggerClassName?: string;
  menuClassName?: string;
};

export function SplitButton({
  primaryAction,
  menuOptions,
  onPrimaryClick,
  onSelect,
  disabled = false,
  className,
  menuAlign = "end",
  primaryClassName,
  triggerClassName,
  menuClassName,
}: SplitButtonProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "group flex w-full rounded-xl shadow-xs transition-all duration-200 focus-within:ring-[3px] focus-within:ring-ring/50",
          disabled && "opacity-50",
          className
        )}
      >
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-9 min-w-0 flex-1 items-center justify-center rounded-l-xl bg-primary px-3 text-[12px] font-medium text-primary-foreground transition-all duration-200",
            "hover:bg-primary hover:shadow-sm active:bg-primary",
            "disabled:pointer-events-none",
            primaryClassName
          )}
          onClick={onPrimaryClick}
        >
          {primaryAction}
        </button>

        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Open more options"
            className={cn(
              "flex h-9 w-10 shrink-0 items-center justify-center rounded-r-xl border-l border-primary-foreground/15 bg-primary text-primary-foreground transition-all duration-200",
              "hover:bg-primary hover:shadow-sm active:bg-primary",
              "disabled:pointer-events-none",
              triggerClassName
            )}
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </button>
        </PopoverTrigger>
      </div>

      <PopoverContent
        align={menuAlign}
        sideOffset={8}
        className={cn(
          "w-[var(--radix-popover-trigger-width)] min-w-[220px] rounded-xl border border-border/60 bg-popover p-1.5 shadow-lg",
          menuClassName
        )}
      >
        <div className="space-y-1">
          {menuOptions.map((option) => {
            const Icon = option.icon;

            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled || option.disabled}
                className={cn(
                  "flex w-full appearance-none items-center rounded-lg border border-transparent bg-transparent px-3 py-2 text-left text-[12px] font-medium text-popover-foreground shadow-none outline-none transition-colors",
                  "hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                  "disabled:pointer-events-none disabled:opacity-50"
                )}
                onClick={() => {
                  setOpen(false);
                  onSelect(option);
                }}
              >
                {Icon ? <Icon className="mr-2 h-3.5 w-3.5" /> : null}
                {option.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
